<template>
  <div class="liquidacion-detalle">
    <div class="header">
      <button class="btn-back" @click="$router.back()">← Volver</button>
      <h1 v-if="liquidacion">{{ liquidacion.numero_formateado }}</h1>
    </div>

    <div v-if="cargando" class="loading">Cargando...</div>

    <div v-else-if="liquidacion" class="detalle">
      <!-- Resumen de cabecera -->
      <div class="summary-card">
        <div class="row">
          <div class="col">
            <div class="field">
              <label>Estado</label>
              <span :class="['estado-badge', `estado-${liquidacion.estado}`]">
                {{ liquidacion.estado }}
              </span>
            </div>
          </div>
          <div class="col">
            <div class="field">
              <label>Período</label>
              <span>
                {{ formatearFecha(liquidacion.periodo_desde) }} -
                {{ formatearFecha(liquidacion.periodo_hasta) }}
              </span>
            </div>
          </div>
          <div class="col">
            <div class="field">
              <label>Forma de pago</label>
              <span>
                {{ liquidacion.periodicidad_display }}
                <em v-if="!liquidacion.periodicidad_propia" class="fuera-de-norma">
                  (habitual: {{ periodicidadHabitual }})
                </em>
              </span>
            </div>
          </div>
          <div class="col">
            <div class="field">
              <label>Cálculo</label>
              <span>{{ liquidacion.base_calculo_display }}</span>
            </div>
          </div>
          <div class="col">
            <div class="field">
              <label>Total Horas</label>
              <span>{{ liquidacion.horas_total }}</span>
            </div>
          </div>
          <div class="col">
            <div class="field">
              <label>Total</label>
              <span class="monto">{{ formatearMoneda(liquidacion.total) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Cómo viene el mes al que se imputa esta liquidación -->
      <div v-if="liquidacion.mes" class="control-mes">
        <div class="control-mes-titulo">Mes de {{ liquidacion.mes.nombre }}</div>
        <div class="control-mes-filas">
          <div>
            <span class="rotulo">Sueldo del mes</span>
            <strong>{{ formatearMoneda(liquidacion.mes.devengado) }}</strong>
          </div>
          <div>
            <span class="rotulo">Liquidado</span>
            <strong>{{ formatearMoneda(liquidacion.mes.liquidado) }}</strong>
          </div>
          <div>
            <span class="rotulo">{{ etiquetaSaldo }}</span>
            <strong :class="claseSaldo">{{ formatearMoneda(saldoAbsoluto) }}</strong>
          </div>
        </div>
      </div>

      <!-- Corregir el período o la forma de pago (solo borrador): recalcula todo -->
      <div v-if="liquidacion.estado === 'borrador'" class="section">
        <h2>Período y forma de pago</h2>

        <form class="form-periodo" @submit.prevent="guardarPeriodo">
          <div class="form-group">
            <label>Forma de pago</label>
            <select v-model="formPeriodo.periodicidad">
              <option
                v-for="opcion in periodicidades"
                :key="opcion.valor"
                :value="opcion.valor"
              >
                {{ opcion.label }}
                {{ opcion.valor === liquidacion.periodicidad_condicion ? '(la habitual)' : '' }}
              </option>
            </select>
          </div>

          <div class="form-group">
            <label>Cómo se calcula</label>
            <select v-model="formPeriodo.base_calculo">
              <option v-for="opcion in basesCalculo" :key="opcion.valor" :value="opcion.valor">
                {{ opcion.label }}
              </option>
            </select>
          </div>

          <div class="form-group">
            <label>Desde</label>
            <input v-model="formPeriodo.periodo_desde" type="date" required />
          </div>

          <div class="form-group">
            <label>Hasta</label>
            <input v-model="formPeriodo.periodo_hasta" type="date" required />
          </div>

          <button type="submit" class="btn-primary" :disabled="cargandoPeriodo">
            {{ cargandoPeriodo ? 'Recalculando...' : 'Guardar y recalcular' }}
          </button>
        </form>

        <p class="ayuda">
          El sueldo se reparte por los días del período dentro del mes. Con
          <strong>cierre de mes</strong> paga lo que falte para completarlo, sea cual sea
          la partición. Cambiar las fechas vuelve a tomar los turnos del período y libera
          los que quedan afuera.
        </p>
      </div>

      <!-- Paso 1: Conceptos automáticos (HORAS y FIJO) -->
      <div class="section">
        <h2>Conceptos Calculados</h2>
        <table class="conceptos-table">
          <thead>
            <tr>
              <th>Tipo</th>
              <th>Descripción</th>
              <th class="numero">Cantidad</th>
              <th class="numero">Valor Unitario</th>
              <th class="numero">Importe</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="concepto in conceptosAutomaticos" :key="concepto.id">
              <td>{{ concepto.tipo }}</td>
              <td>{{ concepto.descripcion }}</td>
              <td class="numero">{{ concepto.cantidad }}</td>
              <td class="numero">{{ formatearMoneda(concepto.valor_unitario) }}</td>
              <td class="numero">{{ formatearMoneda(concepto.importe) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Paso 2: Agregar conceptos manuales (BONO, ADELANTO, DESCUENTO) -->
      <div v-if="liquidacion.estado === 'borrador'" class="section">
        <h2>Conceptos Adicionales</h2>

        <div class="agregar-concepto">
          <div class="form-row">
            <div class="form-group">
              <label>Tipo *</label>
              <select v-model="formConcepto.tipo">
                <option value="BONO">Bono</option>
                <option value="ADELANTO">Adelanto</option>
                <option value="DESCUENTO">Descuento</option>
              </select>
            </div>

            <div class="form-group">
              <label>Descripción *</label>
              <input v-model="formConcepto.descripcion" type="text" />
            </div>

            <div class="form-group">
              <label>Cantidad</label>
              <input v-model.number="formConcepto.cantidad" type="number" min="1" value="1" />
            </div>

            <div class="form-group">
              <label>Valor Unitario *</label>
              <input v-model.number="formConcepto.valor_unitario" type="number" step="0.01" />
            </div>

            <button
              type="button"
              class="btn-primary"
              @click="agregarConcepto"
              :disabled="cargandoConcepto"
            >
              {{ cargandoConcepto ? 'Agregando...' : 'Agregar' }}
            </button>
          </div>
        </div>

        <table v-if="conceptosManuales.length > 0" class="conceptos-table">
          <thead>
            <tr>
              <th>Tipo</th>
              <th>Descripción</th>
              <th class="numero">Cantidad</th>
              <th class="numero">Valor Unitario</th>
              <th class="numero">Importe</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="concepto in conceptosManuales" :key="concepto.id">
              <td>{{ concepto.tipo }}</td>
              <td>{{ concepto.descripcion }}</td>
              <td class="numero">{{ concepto.cantidad }}</td>
              <td class="numero">{{ formatearMoneda(concepto.valor_unitario) }}</td>
              <td class="numero">{{ formatearMoneda(concepto.importe) }}</td>
              <td>
                <button class="btn-link" @click="eliminarConcepto(concepto.id)">
                  Eliminar
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Registros de horas utilizados -->
      <div v-if="liquidacion.registros && liquidacion.registros.length > 0" class="section">
        <h2>Horas Registradas</h2>
        <table class="registros-table">
          <thead>
            <tr>
              <th>Fecha</th>
              <th>Entrada</th>
              <th>Salida</th>
              <th class="numero">Horas</th>
              <th class="numero">Recargo %</th>
              <th>Extra</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="reg in liquidacion.registros" :key="reg.id">
              <td>{{ formatearFecha(reg.fecha) }}</td>
              <td>{{ reg.hora_entrada }}</td>
              <td>{{ reg.hora_salida }}</td>
              <td class="numero">{{ reg.horas }}</td>
              <td class="numero">{{ reg.recargo }}%</td>
              <td>{{ reg.es_extra ? 'Sí' : 'No' }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Paso 3: Confirmar liquidación (solo en borrador) -->
      <div v-if="liquidacion.estado === 'borrador'" class="section">
        <h2>Confirmar Liquidación</h2>

        <div class="resumen-totales">
          <div class="monto-row">
            <span>Subtotal:</span>
            <span class="numero">{{ formatearMoneda(liquidacion.subtotal) }}</span>
          </div>
          <div class="monto-row">
            <span>Diferencia Redondeo:</span>
            <span class="numero">{{ formatearMoneda(liquidacion.diferencia_redondeo) }}</span>
          </div>
          <div class="monto-row total">
            <span>Total:</span>
            <span class="numero">{{ formatearMoneda(liquidacion.total) }}</span>
          </div>
        </div>

        <form @submit.prevent="enviarConfirmacion" class="form-confirmar">
          <div class="form-group">
            <label>Tipo de Egreso *</label>
            <select v-model="formConfirmar.tipo_egreso" required>
              <option value="">Seleccionar tipo de egreso</option>
              <option v-for="tipo in tiposEgreso" :key="tipo.id" :value="tipo.id">
                {{ tipo.descripcion }}
              </option>
            </select>
          </div>

          <div class="form-group">
            <label>Categoría *</label>
            <select v-model="formConfirmar.categoria" required>
              <option value="">Seleccionar categoría</option>
              <option v-for="cat in categorias" :key="cat.id" :value="cat.id">
                {{ cat.name }}
              </option>
            </select>
          </div>

          <div class="form-group">
            <label>Fecha de Pago *</label>
            <input v-model="formConfirmar.fecha_pago" type="date" required />
          </div>

          <div class="form-group checkbox">
            <label>
              <input v-model="formConfirmar.paga_con_caja" type="checkbox" />
              Pagar desde caja
            </label>
          </div>

          <div class="form-actions">
            <button type="button" class="btn-secondary" @click="pedirAnulacion" :disabled="cargandoConfirmar">
              Anular
            </button>
            <button type="submit" class="btn-primary" :disabled="cargandoConfirmar">
              {{ cargandoConfirmar ? 'Confirmando...' : 'Confirmar Liquidación' }}
            </button>
          </div>
        </form>
      </div>

      <!-- Liquidación confirmada -->
      <div v-else-if="liquidacion.estado === 'confirmada'" class="section">
        <div class="confirmacion-info">
          <div class="info-row">
            <span>Egreso:</span>
            <span>#{{ liquidacion.egreso }}</span>
          </div>
          <div class="info-row">
            <span>Fecha Pago:</span>
            <span>{{ formatearFecha(liquidacion.fecha_pago) }}</span>
          </div>
        </div>

        <div class="acciones-confirmadas">
          <button class="btn-secondary" @click="descargarPdf">
            Descargar PDF
          </button>
          <button class="btn-danger" @click="pedirAnulacion" :disabled="cargandoConfirmar">
            {{ cargandoConfirmar ? 'Anulando...' : 'Anular' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Error -->
    <div v-if="error" class="error-alert">
      <p>{{ error }}</p>
      <button @click="error = null">Cerrar</button>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { getApiErrorMessage } from '@/util/apiError';
import { formatearFecha as fechaDeCalendario } from '@/util/fecha';
import SueldosAPI, { PERIODICIDADES_PAGO, BASES_CALCULO } from '@/api/sueldos/sueldos';

export default {
  name: 'LiquidacionDetalle',
  data() {
    return {
      cargando: false,
      cargandoConcepto: false,
      cargandoConfirmar: false,
      cargandoPeriodo: false,
      error: null,
      liquidacion: null,
      tiposEgreso: [],
      categorias: [],
      periodicidades: PERIODICIDADES_PAGO,
      basesCalculo: BASES_CALCULO,
      formPeriodo: {
        periodicidad: '',
        base_calculo: '',
        periodo_desde: '',
        periodo_hasta: '',
      },
      formConcepto: {
        tipo: 'BONO',
        descripcion: '',
        cantidad: 1,
        valor_unitario: 0,
      },
      formConfirmar: {
        tipo_egreso: '',
        categoria: '',
        paga_con_caja: false,
        fecha_pago: new Date().toISOString().split('T')[0],
      },
    };
  },
  computed: {
    ...mapGetters('sueldos', ['liquidacionActual']),
    saldoDelMes() {
      return this.liquidacion && this.liquidacion.mes
        ? Number(this.liquidacion.mes.saldo)
        : 0;
    },
    saldoAbsoluto() {
      return Math.abs(this.saldoDelMes);
    },
    // Un saldo negativo significa que el mes se pagó por encima del sueldo
    etiquetaSaldo() {
      return this.saldoDelMes < 0 ? 'Pagado de más' : 'Falta';
    },
    claseSaldo() {
      if (this.saldoDelMes < 0) return 'saldo-rojo';
      return this.saldoDelMes === 0 ? 'saldo-ok' : '';
    },
    periodicidadHabitual() {
      if (!this.liquidacion) return '';
      const opcion = this.periodicidades.find(
        p => p.valor === this.liquidacion.periodicidad_condicion,
      );
      return opcion ? opcion.label.toLowerCase() : '';
    },
    conceptosAutomaticos() {
      if (!this.liquidacion || !this.liquidacion.conceptos) return [];
      return this.liquidacion.conceptos.filter(c => c.tipo === 'HORAS' || c.tipo === 'FIJO');
    },
    conceptosManuales() {
      if (!this.liquidacion || !this.liquidacion.conceptos) return [];
      return this.liquidacion.conceptos.filter(
        c => c.tipo === 'BONO' || c.tipo === 'ADELANTO' || c.tipo === 'DESCUENTO'
      );
    },
  },
  methods: {
    ...mapActions('sueldos', [
      'fetchLiquidacion',
      'actualizarLiquidacion',
      'agregarConceptoLiquidacion',
      'eliminarConceptoLiquidacion',
      'confirmarLiquidacion',
      'anularLiquidacion',
      'descargarPdfLiquidacion',
    ]),
    async cargarLiquidacion() {
      this.cargando = true;
      try {
        const response = await this.fetchLiquidacion(this.$route.params.id);
        this.liquidacion = response.data;
        this.formPeriodo = {
          periodicidad: this.liquidacion.periodicidad,
          base_calculo: this.liquidacion.base_calculo,
          periodo_desde: this.liquidacion.periodo_desde,
          periodo_hasta: this.liquidacion.periodo_hasta,
        };
      } catch (err) {
        this.error = getApiErrorMessage(err);
      } finally {
        this.cargando = false;
      }
    },
    async cargarTiposEgreso() {
      try {
        this.tiposEgreso = await SueldosAPI.opciones.tiposEgreso();
      } catch (err) {
        this.error = getApiErrorMessage(err);
      }
    },
    async cargarCategorias() {
      try {
        this.categorias = await SueldosAPI.opciones.categorias();
      } catch (err) {
        this.error = getApiErrorMessage(err);
      }
    },
    // El backend recalcula todo: conceptos automáticos, turnos del período y totales
    async guardarPeriodo() {
      this.cargandoPeriodo = true;
      try {
        await this.actualizarLiquidacion({
          id: this.$route.params.id,
          data: { ...this.formPeriodo },
        });
        await this.cargarLiquidacion();
      } catch (err) {
        this.error = getApiErrorMessage(err);
      } finally {
        this.cargandoPeriodo = false;
      }
    },
    async agregarConcepto() {
      this.cargandoConcepto = true;
      try {
        const data = { ...this.formConcepto };
        data.cantidad = parseInt(data.cantidad);
        data.valor_unitario = parseFloat(data.valor_unitario);

        await this.agregarConceptoLiquidacion({
          id: this.$route.params.id,
          data,
        });

        this.formConcepto = {
          tipo: 'BONO',
          descripcion: '',
          cantidad: 1,
          valor_unitario: 0,
        };

        await this.cargarLiquidacion();
      } catch (err) {
        this.error = getApiErrorMessage(err);
      } finally {
        this.cargandoConcepto = false;
      }
    },
    async eliminarConcepto(conceptoId) {
      if (!confirm('¿Eliminar este concepto?')) return;

      try {
        await this.eliminarConceptoLiquidacion({
          id: this.$route.params.id,
          conceptoId,
        });
        await this.cargarLiquidacion();
      } catch (err) {
        this.error = getApiErrorMessage(err);
      }
    },
    async enviarConfirmacion() {
      this.cargandoConfirmar = true;
      try {
        const data = {
          tipo_egreso: parseInt(this.formConfirmar.tipo_egreso),
          categoria: parseInt(this.formConfirmar.categoria),
          fecha_pago: this.formConfirmar.fecha_pago,
          paga_con_caja: this.formConfirmar.paga_con_caja,
        };

        await this.confirmarLiquidacion({
          id: this.$route.params.id,
          data,
        });

        await this.cargarLiquidacion();
      } catch (err) {
        this.error = getApiErrorMessage(err);
      } finally {
        this.cargandoConfirmar = false;
      }
    },
    async pedirAnulacion() {
      if (!confirm('¿Estás seguro? Esta acción liberará las horas asociadas.')) {
        return;
      }

      this.cargandoConfirmar = true;
      try {
        await this.anularLiquidacion(this.$route.params.id);
        await this.cargarLiquidacion();
      } catch (err) {
        this.error = getApiErrorMessage(err);
      } finally {
        this.cargandoConfirmar = false;
      }
    },
    async descargarPdf() {
      try {
        const response = await this.descargarPdfLiquidacion(this.$route.params.id);
        // Descargar el PDF
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `liquidacion-${this.liquidacion.numero}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
      } catch (err) {
        this.error = getApiErrorMessage(err);
      }
    },
    formatearFecha(fecha) {
      return fechaDeCalendario(fecha);
    },
    formatearMoneda(valor) {
      return new Intl.NumberFormat('es-AR', {
        style: 'currency',
        currency: 'ARS',
      }).format(valor || 0);
    },
  },
  mounted() {
    this.cargarLiquidacion();
    this.cargarTiposEgreso();
    this.cargarCategorias();
  },
};
</script>

<style scoped>
.liquidacion-detalle {
  padding: 1rem;
}

.header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.btn-back {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1rem;
  color: #1976d2;
  text-decoration: underline;
}

.loading {
  text-align: center;
  padding: 2rem;
  color: #9e9e9e;
}

.detalle {
  max-width: 1000px;
}

.summary-card {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  padding: 1.5rem;
  border-radius: 8px;
  margin-bottom: 2rem;
}

.row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}

.col {
  display: flex;
  flex-direction: column;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.field label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #9e9e9e;
}

.field span:not(.estado-badge) {
  font-size: 1rem;
  color: #f5f5f5;
}

.monto {
  font-weight: 600;
  font-size: 1.25rem;
}

.estado-badge {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 4px;
  font-size: 0.875rem;
  font-weight: 500;
  width: fit-content;
}

.estado-borrador {
  background-color: #fff3cd;
  color: #856404;
}

.estado-confirmada {
  background-color: #d4edda;
  color: #155724;
}

.estado-anulada {
  background-color: #f8d7da;
  color: #721c24;
}

.section {
  margin-bottom: 2rem;
}

.section h2 {
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.conceptos-table,
.registros-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
}

.conceptos-table thead,
.registros-table thead {
  background-color: rgba(255, 255, 255, 0.04);
}

.conceptos-table th,
.registros-table th {
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  color: #f5f5f5;
  border-bottom: 2px solid rgba(255, 255, 255, 0.16);
  font-size: 0.95rem;
}

.conceptos-table td,
.registros-table td {
  padding: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.conceptos-table tr:hover,
.registros-table tr:hover {
  background-color: rgba(255, 255, 255, 0.03);
}

.numero {
  text-align: right;
  font-family: 'Courier New', monospace;
}

.agregar-concepto {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  padding: 1.5rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
}

.form-row,
.form-periodo {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
  align-items: flex-end;
}

.ayuda {
  margin: 0.6rem 0 0 0;
  font-size: 0.8rem;
  color: #9e9e9e;
}

.control-mes {
  padding: 0.9rem 1.1rem;
  margin-bottom: 1.5rem;
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 8px;
}

.control-mes-titulo {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #9e9e9e;
  margin-bottom: 0.65rem;
}

.control-mes-filas {
  display: flex;
  flex-wrap: wrap;
  gap: 2rem;
}

.control-mes-filas .rotulo {
  display: block;
  font-size: 0.75rem;
  color: #9e9e9e;
  margin-bottom: 0.15rem;
}

.control-mes-filas strong {
  color: #f5f5f5;
  font-size: 1.05rem;
}

.saldo-rojo { color: #ef5350; }
.saldo-ok { color: #66bb6a; }

.fuera-de-norma {
  font-style: normal;
  font-size: 0.8rem;
  color: #f0c674;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.form-group label {
  font-weight: 500;
  font-size: 0.95rem;
}

.form-group input,
.form-group select {
  padding: 0.5rem;
  background-color: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 4px;
  color: #f5f5f5;
  font-size: 0.95rem;
  font-family: inherit;
}

.form-group select option {
  background-color: #1a1a1a;
  color: #f5f5f5;
}

.form-group input[type="date"] {
  color-scheme: dark;
}

.form-group.checkbox {
  flex-direction: row;
  align-items: center;
}

.form-group.checkbox input {
  width: auto;
  margin-right: 0.5rem;
}

.resumen-totales {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  padding: 1.5rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
}

.monto-row {
  display: flex;
  justify-content: space-between;
  padding: 0.5rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.monto-row.total {
  font-weight: 600;
  font-size: 1.1rem;
  border: none;
  padding: 1rem 0 0 0;
  margin-top: 0.5rem;
}

.form-confirmar {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  padding: 1.5rem;
  border-radius: 8px;
}

.form-confirmar .form-group {
  margin-bottom: 1rem;
}

.form-confirmar .form-group:last-of-type {
  margin-bottom: 1.5rem;
}

.form-actions {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
}

.btn-primary {
  padding: 0.75rem 1.5rem;
  background-color: #1976d2;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.95rem;
  transition: background-color 0.2s;
}

.btn-primary:hover:not(:disabled) {
  background-color: #1565c0;
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  padding: 0.75rem 1.5rem;
  background-color: rgba(255, 255, 255, 0.08);
  color: #f5f5f5;
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.95rem;
}

.btn-secondary:hover:not(:disabled) {
  background-color: rgba(255, 255, 255, 0.14);
}

.btn-danger {
  padding: 0.75rem 1.5rem;
  background-color: #d32f2f;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.95rem;
  transition: background-color 0.2s;
}

.btn-danger:hover:not(:disabled) {
  background-color: #c62828;
}

.btn-danger:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-link {
  background: none;
  border: none;
  color: #d32f2f;
  cursor: pointer;
  text-decoration: underline;
  padding: 0;
  font-size: 0.95rem;
}

.btn-link:hover {
  color: #c62828;
}

.confirmacion-info {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  padding: 1.5rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
}

.info-row {
  display: flex;
  justify-content: space-between;
  padding: 0.5rem 0;
}

.acciones-confirmadas {
  display: flex;
  gap: 0.5rem;
}

.error-alert {
  padding: 1rem;
  background-color: rgba(244, 67, 54, 0.12);
  border: 1px solid rgba(244, 67, 54, 0.4);
  border-radius: 4px;
  color: #ff8a80;
  margin-top: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.error-alert button {
  background: none;
  border: none;
  color: #ff8a80;
  cursor: pointer;
  text-decoration: underline;
}
</style>
